import {
  Badge,
  Button,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@devhop/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import type { CfOrderListItem } from "@/modules/cf-order/presentation/api/types";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { workspaceRequest } from "../api/workspace-client";
import { kip, WorkspaceSelect } from "./WorkspaceProductEditor";

const statusItems: Array<[string, string]> = [
  ["all", "ທຸກສະຖານະ"],
  ["pending", "ລໍຖ້າ"],
  ["confirmed", "ຢືນຢັນ"],
  ["paid", "ຈ່າຍແລ້ວ"],
  ["shipped", "ຈັດສົ່ງ"],
  ["delivered", "ສົ່ງຮອດ"],
  ["cancelled", "ຍົກເລີກ"],
];
export function WorkspaceOrders({ id }: { id: string }) {
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [filters, setFilters] = useState({
    status: "all",
    payment: "all",
    source: "all",
    from: "",
    until: "",
  });
  useEffect(() => {
    const timer = setTimeout(() => setSearch(q), 300);
    return () => clearTimeout(timer);
  }, [q]);
  useEffect(() => setOffset(0), [search, filters]);
  const params = new URLSearchParams({
    q: search,
    offset: String(offset),
    limit: "20",
  });
  for (const [key, value] of Object.entries(filters))
    if (value && value !== "all") params.set(key, value);
  const orders = useQuery({
    queryKey: ["workspace-orders", id, params.toString()],
    queryFn: ({ signal }) =>
      workspaceRequest<{
        items: Array<CfOrderListItem & { isPaid: boolean }>;
        total: number;
      }>(`/${id}/orders?${params}`, undefined, signal),
    refetchInterval: 3000,
    retry: false,
  });
  return (
    <div className="space-y-4">
      <Input
        className="min-h-11"
        aria-label="ຄົ້ນຫາອໍເດີ"
        placeholder="ລະຫັດອໍເດີ, ຊື່ລູກຄ້າ, ລະຫັດ CF"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <WorkspaceSelect
          label="ສະຖານະ"
          value={filters.status}
          onChange={(status) => setFilters({ ...filters, status })}
          items={statusItems}
        />
        <WorkspaceSelect
          label="ການຊຳລະ (Admin)"
          value={filters.payment}
          onChange={(payment) => setFilters({ ...filters, payment })}
          items={[
            ["all", "ທັງໝົດ"],
            ["paid", "ຊຳລະແລ້ວ"],
            ["unpaid", "ຍັງບໍ່ຊຳລະ"],
          ]}
        />
        <WorkspaceSelect
          label="ແຫຼ່ງທີ່ມາ"
          value={filters.source}
          onChange={(source) => setFilters({ ...filters, source })}
          items={[
            ["all", "ທັງໝົດ"],
            ["comment", "ຄອມເມັນ"],
            ["message", "ຂໍ້ຄວາມ"],
            ["manual", "ແອດມິນ"],
          ]}
        />
        {(["from", "until"] as const).map((key) => (
          <label
            htmlFor={`workspace-orders-${id}-${key}`}
            key={key}
            className="block space-y-1 text-sm"
          >
            <span>{key === "from" ? "ຈາກເວລາ" : "ເຖິງເວລາ"} (UTC+7)</span>
            <Input
              id={`workspace-orders-${id}-${key}`}
              type="datetime-local"
              className="min-h-11"
              value={filters[key]}
              onChange={(e) =>
                setFilters({ ...filters, [key]: e.target.value })
              }
            />
          </label>
        ))}
      </div>
      {orders.isError ? (
        <p role="alert">ໂຫຼດອໍເດີບໍ່ສຳເລັດ. ກວດຕົວກອງເວລາ ແລະລອງໃໝ່</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ເລກອໍເດີ</TableHead>
                <TableHead>ລູກຄ້າ / CF</TableHead>
                <TableHead>ຍອດຮັບ CF</TableHead>
                <TableHead>ສະຖານະ</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.data?.items.map((order) => (
                <TableRow key={order.id}>
                  <TableCell>
                    <strong>{order.shopOrderCode ?? order.id}</strong>
                    <span className="block text-muted-foreground text-xs">
                      {formatDateTimeLocal(order.createdAt)}
                    </span>
                  </TableCell>
                  <TableCell>
                    {order.customerName ?? "—"}
                    <span className="block">
                      {order.cfCodeUsed} × {order.quantity}
                    </span>
                  </TableCell>
                  <TableCell>{kip(order.totalPrice)}</TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {statusItems.find(([key]) => key === order.status)?.[1] ??
                        order.status}
                    </Badge>
                    <span className="mt-1 block text-xs">
                      {order.isPaid ? "ຊຳລະແລ້ວ (Admin)" : "ຍັງບໍ່ຊຳລະ"}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
              {!orders.data?.items.length && (
                <TableRow>
                  <TableCell colSpan={4} className="py-8 text-center">
                    {orders.isLoading ? "ກຳລັງໂຫຼດ…" : "ບໍ່ພົບອໍເດີ"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          className="min-h-11"
          disabled={!offset}
          onClick={() => setOffset(Math.max(0, offset - 20))}
        >
          ກ່ອນໜ້າ
        </Button>
        <span className="text-sm">{orders.data?.total ?? 0} ລາຍການ</span>
        <Button
          variant="outline"
          className="min-h-11"
          disabled={offset + 20 >= (orders.data?.total ?? 0)}
          onClick={() => setOffset(offset + 20)}
        >
          ໜ້າຖັດໄປ
        </Button>
      </div>
    </div>
  );
}
